import { useState } from "react";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { User as UserIcon, Mail, Lock, Save, Shield, Check, Loader2 } from "lucide-react";

export default function AccountPage() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  const dirty =
    name !== (user?.name || "") ||
    email.toLowerCase() !== (user?.email || "").toLowerCase() ||
    newPassword.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setDone("");

    if (newPassword && newPassword !== newPasswordConfirm) {
      setErr("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }

    const payload = { current_password: currentPassword };
    if (name !== (user?.name || "")) payload.name = name;
    if (email.toLowerCase() !== (user?.email || "").toLowerCase()) payload.email = email;
    if (newPassword) payload.new_password = newPassword;

    setBusy(true);
    try {
      await api.put("/auth/me", payload);
      await refresh();
      setDone("Vos informations ont été mises à jour.");
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const emailWillChange =
    email.toLowerCase().trim() !== (user?.email || "").toLowerCase().trim() && email.trim() !== "";

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-3xl" data-testid="account-page">
        <div className="label-xs mb-2">Mon compte</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Préférences personnelles
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          Modifiez votre nom, votre adresse e-mail et votre mot de passe. Pour des raisons de sécurité,
          chaque modification nécessite la confirmation de votre mot de passe actuel.
        </p>

        {(user?.role === "admin" || user?.role === "superadmin") && (
          <div className="mt-5 inline-flex items-center gap-2 text-xs px-2.5 py-1 bg-blue-50 text-[#2563eb] rounded-sm font-semibold" data-testid="account-role-badge">
            <Shield size={12} /> {user.role === "superadmin" ? "Compte super-administrateur" : "Compte administrateur"}
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="account-form">
          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="label-xs mb-4">Informations</div>
            <div className="space-y-4">
              <Field label="Nom complet" icon={<UserIcon size={13} />}>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="account-name-input"
                  className="input"
                />
              </Field>
              <Field label="Adresse e-mail" icon={<Mail size={13} />}>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="account-email-input"
                  className="input"
                />
                {emailWillChange && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 mt-2 px-2.5 py-1.5 rounded-sm" data-testid="email-change-warning">
                    ⚠️ Vous changez votre e-mail de <strong>{user?.email}</strong> à <strong>{email}</strong>.
                    Vous devrez utiliser le nouvel e-mail à la prochaine connexion.
                  </p>
                )}
              </Field>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="label-xs mb-4">Changer mon mot de passe (facultatif)</div>
            <div className="space-y-4">
              <Field label="Nouveau mot de passe" icon={<Lock size={13} />}>
                <input
                  type="text"
                  value={newPassword}
                  minLength={6}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Laisser vide pour ne pas changer"
                  data-testid="account-new-password-input"
                  className="input font-mono"
                />
              </Field>
              {newPassword && (
                <Field label="Confirmer le nouveau mot de passe" icon={<Lock size={13} />}>
                  <input
                    type="text"
                    value={newPasswordConfirm}
                    minLength={6}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    data-testid="account-new-password-confirm-input"
                    className="input font-mono"
                  />
                </Field>
              )}
            </div>
          </section>

          <section className="bg-slate-900 text-white rounded-md p-6">
            <div className="label-xs text-white/60 mb-4">Confirmation</div>
            <Field label="Votre mot de passe actuel" white>
              <input
                required
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="account-current-password-input"
                className="w-full px-3 py-2 text-sm bg-white text-slate-900 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-white focus:border-white"
                placeholder="••••••••"
              />
            </Field>
            <p className="text-xs text-slate-300 mt-3">
              Sans ce mot de passe, aucune modification n'est appliquée. C'est notre garde-fou contre les
              changements non autorisés si quelqu'un accédait à votre session.
            </p>
          </section>

          {err && (
            <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="account-error">
              {err}
            </div>
          )}
          {done && (
            <div className="text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="account-success">
              <Check size={14} /> {done}
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={!dirty || !currentPassword || busy}
              data-testid="account-submit-btn"
              className="inline-flex items-center gap-2 bg-[#2563eb] text-white px-5 py-2.5 rounded-sm font-semibold text-sm hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer les modifications
            </button>
          </div>
        </form>

        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.875rem; border: 1px solid #cbd5e1; border-radius: 2px; }
          .input:focus { outline: none; border-color: #0f172a; box-shadow: 0 0 0 1px #0f172a; }`}</style>
      </div>
    </AppShell>
  );
}

function Field({ label, icon, white, children }) {
  return (
    <div>
      <label className={`label-xs ${white ? "text-white/70" : ""} flex items-center gap-1.5 mb-2`}>
        {icon} {label}
      </label>
      {children}
    </div>
  );
}
